import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select, Tabs } from "../components/DesignSystem";
import type { EventInput, ID, Scope, WeddingEvent } from "../domain/types";
import { errorMessage } from "../services/errors";
import type { RouteKey } from "../shell/PlannerShell";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, formatShortDate, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

const emptyEvent: EventInput = { name: "", type: "Custom", date: "", time: "18:00", venue: "", status: "planned", custom: true };

export function EventsScreen({ scope, setScope, go }: { scope: Scope; setScope: (scope: Scope) => void; go: (route: RouteKey) => void }) {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [editing, setEditing] = React.useState<WeddingEvent | "new" | null>(null);
  const [editingAttempt, setEditingAttempt] = React.useState(0);
  const [deleting, setDeleting] = React.useState<WeddingEvent | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const { submitting: savingEvent, guardedSubmit: guardedSaveEvent } = useSubmitGuard();
  const { submitting: deletingEvent, guardedSubmit: guardedDeleteEvent } = useSubmitGuard();
  const eventIdempotencyKey = useIdempotencyKey(editingAttempt);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading events" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const rows = scope === "all" || scope === "shared" ? workspace.events : workspace.events.filter((event) => event.id === scope);
  const deleteEvent = () => guardedDeleteEvent(async () => {
    if (!deleting) return;
    setActionError(null);
    try {
      await run(() => service.events.deleteEvent(deleting.id));
      if (scope === deleting.id) setScope("all");
      setDeleting(null);
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });

  return (
    <div>
      <PageHeader
        eyebrow={scope === "all" ? "Plan" : "Event scope"}
        title="Events"
        meta={[
          { icon: "calendar-days", label: `${workspace.events.length} celebrations` },
          { icon: "layers", label: scope === "all" ? "Whole Wedding" : scope === "shared" ? "Shared state" : rows[0]?.name }
        ]}
        actions={<><Button onClick={() => setScope("all")}>Whole Wedding</Button><Button variant="primary" icon="plus" onClick={() => { setEditingAttempt((n) => n + 1); setEditing("new"); }}>Create event</Button></>}
        tabs={<Tabs value={scope === "shared" ? "all" : String(scope)} onChange={(value) => setScope(value as Scope)} tabs={[{ value: "all", label: "Whole Wedding", count: workspace.events.length }, ...workspace.events.map((event) => ({ value: event.id, label: event.name }))]} />}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="error" title="Event action failed">{actionError}</Callout>}
        <Callout tone="neutral" title="Event scope is a lens">
          Whole Wedding shows every celebration. Choosing a celebration scopes event-aware screens without creating duplicate records.
        </Callout>
        <Card padding="0">
          <DataTable
            rows={rows}
            onRowClick={(event) => setEditing(event)}
            emptyLabel="No celebrations in this scope"
            columns={[
              { key: "name", header: "Celebration", render: (event) => <span style={{ font: "var(--type-label)" }}>{event.name}</span> },
              { key: "date", header: "Date", render: (event) => formatShortDate(event.date), muted: true },
              { key: "time", header: "Time", muted: true },
              { key: "venue", header: "Venue", muted: true },
              { key: "type", header: "Type" },
              { key: "status", header: "Status", render: (event) => <Badge tone={event.status === "confirmed" ? "success" : "neutral"}>{event.status}</Badge> },
              { key: "actions", header: "", width: 170, render: (event) => <span style={{ display: "flex", gap: "var(--space-2)" }}><Button size="sm" onClick={(e) => { e.stopPropagation(); setEditing(event); }}>Edit</Button><Button size="sm" variant="danger" onClick={(e) => { e.stopPropagation(); setDeleting(event); }}>Delete</Button></span> }
            ]}
          />
        </Card>
      </div>
      {editing && <EventDialog submitting={savingEvent} event={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={(input) => guardedSaveEvent(async () => {
        setActionError(null);
        try {
          if (editing === "new") await run(() => service.events.createEvent(input, eventIdempotencyKey));
          else await run(() => service.events.updateEvent(editing.id, input));
          setEditing(null);
        } catch (err) {
          setActionError(errorMessage(err));
        }
      })} />}
      {deleting && <Dialog title={`Delete ${deleting.name}?`} description="This removes the event and clears event-specific invitations and RSVP answers." onClose={() => setDeleting(null)} footer={<><Button size="sm" onClick={() => setDeleting(null)} disabled={deletingEvent}>Cancel</Button><Button size="sm" variant="danger" disabled={deletingEvent} onClick={deleteEvent}>{deletingEvent ? "Deleting…" : "Delete event"}</Button></>}>
        <Callout tone="error" title="Confirmation required">This destructive event change is saved to the server.</Callout>
      </Dialog>}
    </div>
  );
}

function EventDialog({ event, onClose, onSave, submitting }: { event: WeddingEvent | null; onClose: () => void; onSave: (input: EventInput) => void; submitting?: boolean }) {
  const [draft, setDraft] = React.useState<EventInput>(event ? { name: event.name, type: event.type, date: event.date, time: event.time, venue: event.venue, status: event.status, custom: event.custom, visibleOnWebsite: event.visibleOnWebsite } : emptyEvent);
  const valid = draft.name.trim() && draft.date && draft.time && draft.venue.trim();
  return (
    <Dialog title={event ? `Edit ${event.name}` : "Create event"} description={event ? "Update celebration details." : "Custom celebrations are first-class events."} onClose={onClose} footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => onSave(draft)}>{submitting ? "Saving…" : event ? "Save" : "Create"}</Button></>}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Field label="Name"><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
        <Field label="Type"><Select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value, custom: e.target.value === "Custom" })} options={["Mehndi", "Nikkah", "Baraat", "Walima", "Ceremony", "Reception", "Custom"]} /></Field>
        <div className="pob-grid-2">
          <Field label="Date"><Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} /></Field>
          <Field label="Time"><Input type="time" value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} /></Field>
        </div>
        <Field label="Venue"><Input value={draft.venue} onChange={(e) => setDraft({ ...draft, venue: e.target.value })} /></Field>
        <Field label="Status"><Select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as WeddingEvent["status"] })} options={[{ value: "planned", label: "Planned" }, { value: "tentative", label: "Tentative" }, { value: "confirmed", label: "Confirmed" }]} /></Field>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", font: "var(--type-body-sm)" }}>
          <input type="checkbox" checked={Boolean(draft.visibleOnWebsite)} onChange={(e) => setDraft({ ...draft, visibleOnWebsite: e.target.checked })} />
          Show on wedding website
        </label>
      </div>
    </Dialog>
  );
}
